import {
  EXPORT_COLUMN_GROUPS,
  type ExportColumnGroup,
  MANDATORY_EXPORT_COLUMN_IDS,
  NETWORK_EXPORT_COLUMNS,
} from './export-network-columns.js';

/**
 * Columns the preview shows real cells for.
 *
 * Deliberately only the five the loaded network rows already carry. Showing a
 * cell for anything else would mean inventing a value, and a made-up
 * "Collaboration status" in a preview of the user's own data is worse than no
 * column at all.
 */
export const PREVIEW_COLUMN_IDS = [
  'id',
  'name',
  'officialName',
  'country',
  'city',
] as const;

export type PreviewColumnId = (typeof PREVIEW_COLUMN_IDS)[number];

/**
 * The columns a payload's `columns.keys` will actually produce, in sheet order.
 *
 * Mirrors the backend's `buildSheetColumns`: the list is authoritative, unknown
 * ids are dropped, and the company id is always present.
 */
export function includedColumnIds(keys: readonly string[]): string[] {
  const selected = new Set(keys);
  return NETWORK_EXPORT_COLUMNS.filter(
    column =>
      MANDATORY_EXPORT_COLUMN_IDS.includes(column.id) ||
      selected.has(column.id),
  ).map(column => column.id);
}

/** The preview's visible columns — those we hold values for and are exporting. */
export function previewColumnIds(keys: readonly string[]): PreviewColumnId[] {
  const included = new Set(includedColumnIds(keys));
  return PREVIEW_COLUMN_IDS.filter(id => included.has(id));
}

export interface ExportColumnGroupSummary {
  group: ExportColumnGroup;
  columnIds: string[];
}

/**
 * The exported columns the preview could not show, grouped.
 *
 * Excludes the previewed ones on purpose: the toggle promises "24 more", so
 * listing all 29 would make anyone recount.
 */
export function remainingColumnGroups(
  keys: readonly string[],
): ExportColumnGroupSummary[] {
  const included = new Set(includedColumnIds(keys));
  const previewed = new Set<string>(previewColumnIds(keys));

  return EXPORT_COLUMN_GROUPS.map(group => ({
    group,
    columnIds: NETWORK_EXPORT_COLUMNS.filter(
      column =>
        column.group === group &&
        included.has(column.id) &&
        !previewed.has(column.id),
    ).map(column => column.id),
  })).filter(summary => summary.columnIds.length > 0);
}

/** How many exported columns the preview is not showing. */
export function remainingColumnCount(keys: readonly string[]): number {
  return includedColumnIds(keys).length - previewColumnIds(keys).length;
}
